import { useRef, useEffect } from 'react';

const ACK_SCRIPT = `
  window.addEventListener('message', (event) => {
    if (!event.data || event.data.type !== 'SET_CONTENT') return;

    const sendAck = () => window.parent.postMessage({ type: 'CONTENT_RECEIVED' }, '*');

    try {
      const message = event.data.message;
      const contentArray = typeof message === 'string' ? JSON.parse(message) : message;

      // Check if contentArray is an array and has elements
      if (!Array.isArray(contentArray) || contentArray.length === 0) {
        sendAck();
        return;
      }

      // Check if all items in contentArray have an id and are present in the DOM
      const allFound = contentArray.every(item => item && item.id && document.getElementById(item.id));

      // If all items are found, send acknowledgement
      if (allFound) {
        sendAck();
        return;
      }

      // Fallback for possible Next.js hydration error
      const nextDiv = document.getElementById('__next');
      if (nextDiv && nextDiv.textContent === '') {
        console.warn('[SFPB - CMS | IFRAME] The Next.js application probably crashed 🚨 Sending acknowledgement to CMS 📨➡️', nextDiv);
        sendAck();
      }
    } catch (e) {
      console.warn('[SFPB - CMS | IFRAME] Error parsing IDs or checking DOM 🚨', e);
      
      // Still send acknowledgement on error to prevent the parent from getting stuck.
      sendAck();
    }
  });
`;
export function useIframeContentSync({ iFrameRef, content, onParentReceive, onIframeLoad, isSourceAvailable = true, pollerFrequency = 10 }) {
  const contentReceivedRef = useRef(false);

  // Send content to iframe
  const handleLoad = () => {
    const message = JSON.stringify(content);
    iFrameRef.current?.contentWindow?.postMessage({ type: 'SET_CONTENT', message }, '*');
  };

  // Burst poller
  const burstLoad = () => {
    const interval = 10;
    let pollCount = 0;
    contentReceivedRef.current = false;

    const poll = () => {
      if (!isSourceAvailable || contentReceivedRef.current || pollCount >= pollerFrequency) return;

      handleLoad();
      pollCount++;
      setTimeout(poll, interval);
    };

    poll();
  };

  // Inject ACK script into iframe
  const injectContentAckListener = () => {
    const iframe = iFrameRef.current;
    if (!iframe) return;
    try {
      const script = iframe.contentDocument.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = ACK_SCRIPT;
      iframe.contentDocument.head.appendChild(script);
    } catch (e) {
      // Cross-origin iframes will throw here
      // Optionally handle or ignore
      console.warn('[SFPB - CMS] ERROR | Could not inject script into iframe:', e);
    }
  };

  // Listen for ACK from iframe
  useEffect(() => {
    const handleIframeMessages = (event) => {
      if (!event.data.type) return;

      onParentReceive && onParentReceive(event.data);

      switch (event.data.type) {
        case 'CONTENT_RECEIVED':
          contentReceivedRef.current = true;
          break;
        default:
          console.log('[SFPB - CMS] Unknown message type:', event.data.type);
      }
    };

    window.addEventListener('message', handleIframeMessages);

    return () => window.removeEventListener('message', handleIframeMessages);
  }, [onParentReceive]);

  // Skip first render
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) {
      handleLoad();
    } else {
      didMountRef.current = true;
    }
  }, [content]);

  // Handler for iframe onLoad
  const handleIframeLoad = () => {
    injectContentAckListener();
    burstLoad();
    onIframeLoad && onIframeLoad();
  };

  return {
    handleIframeLoad,
    handleLoad,
    burstLoad,
    injectContentAckListener,
    contentReceivedRef,
  };
}
